import React, { useState, useEffect, useRef } from "react";
import { SportForm, getSportForms } from "api/courts";
import { Button, Grid, Select, MenuItem, LinearProgress, Box, FormControl, InputLabel, Avatar } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { listTimeZones } from "timezone-support";
import { removeClubLogo, patchClubs, uploadClubLogo } from "api/admin/clubs";
import { Club } from "api/clubs";
import { getCoordinatesByAddress } from "utils/geocode";
import { GridTextField } from "components";
import uI18n from "utils/i18n";
import i18n from "./i18n";
import "./clubStyle.scss";

// prop types
interface GeneralTabProps {
    club?: Club,
    onFetchUserProfile: () => void,
}

// general club settings tab
const GeneralTab = ({ club, onFetchUserProfile }: GeneralTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [addresseeName, setAddresseeName] = useState("");
    const [city, setCity] = useState("");
    const [clubDescription, setClubDescription] = useState("");
    const [clubEmail, setClubEmail] = useState("");
    const [clubKey, setClubKey] = useState("");
    const [clubName, setClubName] = useState("");
    const [clubPhone, setClubPhone] = useState("");
    const [clubWebsite, setClubWebsite] = useState("");
    const [country, setCountry] = useState("");
    const [generalSportForms, setGeneralSportForms] = useState<SportForm[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [mainSportForm, setMainSportForm] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [shortName, setShortName] = useState("");
    const [stateOrProvince, setStateOrProvince] = useState("");
    const [street, setStreet] = useState("");
    const [timeZone, setTimeZone] = useState("");
    const inputPicRef = useRef<HTMLInputElement>(null);

    const isCompany = club?.capabilities.includes("company");

    // save action
    const saveAction = async () => {
        setLoading(true);
        try {
            await patchClubs({
                addresseeName,
                city,
                clubDescription,
                clubEmail,
                clubIds: [club!.id],
                clubKey,
                clubName,
                clubPhone,
                clubWebsite: clubWebsite || undefined,
                country,
                latitude: parseFloat(latitude),
                longitude: parseFloat(longitude),
                mainSportForm,
                postalCode,
                shortName,
                stateOrProvince,
                street,
                timeZone,
            });
            onFetchUserProfile(); // refresh clubs
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // fetch all sport forms
    const fetchGeneralSportForms = async () => { setGeneralSportForms(await getSportForms()); };
    useEffect(() => { fetchGeneralSportForms(); }, []);

    // update fields
    useEffect(() => {
        if (club) {
            setAddresseeName(club.addresseeName);
            setCity(club.city);
            setClubDescription(club.clubDescription || "");
            setClubEmail(club.clubEmail || "");
            setClubKey(club.clubKey || "");
            setClubName(club.clubName);
            setClubPhone(club.clubPhone || "");
            setClubWebsite(club.clubWebsite || "");
            setCountry(club.country);
            setLatitude(club.latitude.toString());
            setLongitude(club.longitude.toString());
            setMainSportForm(club.mainSportForm);
            setPostalCode(club.postalCode);
            setShortName(club.shortName);
            setStateOrProvince(club.stateOrProvince);
            setStreet(club.street);
            setTimeZone(club.timeZone);
        }
    }, [club]);

    // update coordinates by geocoding address
    const updateCoordinates = async () => {
        const address = `${street} ${postalCode} ${city} ${stateOrProvince} ${country}`;
        const result = await getCoordinatesByAddress(address);
        setLatitude(result.latitude.toString());
        setLongitude(result.longitude.toString());
    };

    // change club logo
    const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files![0];
        const allowedMime = ["image/png", "image/jpg", "image/jpeg", "image/gif"];
        if (file) {
            if (file.size > 3000000) {
                enqueueSnackbar(i18n.fileTooLarge, { variant: "error" });
            } else if (!allowedMime.includes(file.type)) {
                enqueueSnackbar(i18n.invalidImageFile, { variant: "error" });
            } else {
                try {
                    const response = await uploadClubLogo({
                        clubId: club!.id,
                        clubLogo: file,
                    });
                    if (response) {
                        onFetchUserProfile(); // refresh clubs
                        enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
                    }
                } catch (error) {
                    displayError(error);
                }
            }
        }
    };

    // remove club logo
    const handleRemoveLogo = async () => {
        try {
            const response = await removeClubLogo({
                clubId: club!.id,
            });
            if (response) {
                onFetchUserProfile(); // refresh clubs
                enqueueSnackbar(i18n.deletedSuccessfully, { variant: "success" });
            }
        } catch (error) {
            displayError(error);
        }
    };

    // render
    const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
    return (
        <Box className="club_box">
            <Grid container spacing={2}>
                <Grid item xs={12} className="club_display_center">
                    <Avatar src={club?.clubLogoUrl} className="club_avatar" />
                </Grid>
                <input accept="image/*" ref={inputPicRef} className="club_hide" type="file" onChange={handleUploadLogo} />
                <Grid item xs={12} className="club_display_center">
                    <Button variant="contained" onClick={() => inputPicRef.current!.click()} disabled={isLoading}>
                        {i18n.change}
                    </Button>
                    <Button variant="contained" onClick={handleRemoveLogo} disabled={isLoading} className="club_left_8">
                        {i18n.remove}
                    </Button>
                </Grid>
                {/* club general */}
                <GridTextField size={12} label={i18n.name} value={clubName} onChange={setClubName} />
                <GridTextField size={6} label={i18n.shortName} maxLength={8} value={shortName} onChange={setShortName} />
                <GridTextField size={6} label={(isCompany ? i18n.organisationKey : i18n.clubKey)} maxLength={36} value={clubKey} onChange={setClubKey} />
                <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{i18n.mainSportForm}</InputLabel>
                        <Select
                            label={i18n.mainSportForm}
                            value={mainSportForm}
                            onChange={(e) => setMainSportForm(e.target.value as string)}
                        >
                            {generalSportForms.map((t) => (
                                <MenuItem key={t.name} disabled={!t.available} value={t.name}>{uI18n.sportForms[t.name] || capitalize(t.name)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{i18n.timeZone}</InputLabel>
                        <Select
                            label={i18n.timeZone}
                            value={timeZone}
                            onChange={(e) => setTimeZone(e.target.value as string)}
                        >
                            {listTimeZones().map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <GridTextField size={4} label={i18n.latitude} value={latitude} onChange={(v) => setLatitude(v)} />
                <GridTextField size={4} label={i18n.longitude} value={longitude} onChange={(v) => setLongitude(v)} />
                <Grid item xs={4} style={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={updateCoordinates}>
                        {i18n.fromAddress}
                    </Button>
                </Grid>
                <GridTextField size={12} label={i18n.clubDescription} maxLength={330} value={clubDescription} onChange={setClubDescription} />
                <GridTextField size={12} label={i18n.clubWebsite} maxLength={50} value={clubWebsite} onChange={setClubWebsite} />
                <GridTextField size={6} label={i18n.clubEmail} maxLength={100} value={clubEmail} onChange={setClubEmail} />
                <GridTextField size={6} label={i18n.clubPhone} maxLength={25} value={clubPhone} onChange={setClubPhone} />

                {/* addressee */}
                <GridTextField size={4} label={i18n.addresseeName} value={addresseeName} onChange={setAddresseeName} />
                <GridTextField size={4} label={i18n.street} value={street} onChange={setStreet} />
                <GridTextField size={4} label={i18n.postalCode} value={postalCode} onChange={setPostalCode} />
                <GridTextField size={4} label={i18n.city} value={city} onChange={setCity} />
                <GridTextField size={4} label={i18n.stateOrProvince} value={stateOrProvince} onChange={setStateOrProvince} />
                <GridTextField size={4} label={i18n.country} value={country} onChange={setCountry} />

                <Grid item xs={12}>
                    {isLoading && <LinearProgress />}
                </Grid>
            </Grid>

            {/* save button */}
            <Button
                color="primary"
                disabled={isLoading}
                style={{ margin: "16px" }}
                variant="contained"
                onClick={saveAction}
            >
                {i18n.save}
            </Button>
        </Box>
    );
};

export default GeneralTab;
